import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Logo } from '@/components/ui/Logo';
import { useNavigate } from 'react-router-dom';
import { 
  Plane, MapPin, Luggage, Sun, Umbrella, Thermometer, 
  CheckCircle2, ArrowRight, Download, Star, ArrowLeft
} from 'lucide-react';

// Destination data for SEO pages
const destinationData: Record<string, {
  name: string;
  country: string;
  description: string;
  climate: string;
  bestTime: string;
  essentials: string[];
  tips: string[];
  weather: { season: string; temp: string; condition: string }[];
}> = {
  japan: {
    name: 'Japan',
    country: 'Japan',
    description: 'Experience the perfect blend of ancient traditions and cutting-edge technology. From serene temples in Kyoto to the neon-lit streets of Tokyo.',
    climate: 'Temperate with four distinct seasons. Dry season: December-February. Rainy season (Tsuyu): June-July. Best time to visit: March-May for cherry blossoms or October-November for autumn colors.',
    bestTime: 'March-May (cherry blossoms) or October-November (autumn colors). Avoid rainy season in June-July.',
    essentials: [
      'Portable WiFi or SIM card',
      'Comfortable walking shoes',
      'JR Pass for train travel',
      'Cash (many places are cash-only)',
      'Modest clothing for temples',
      'Power adapter (Type A/B)',
      'Pocket tissues',
      'Hand towel'
    ],
    tips: [
      'Learn basic Japanese phrases',
      'Remove shoes when entering homes/temples',
      'Bow as a greeting',
      'Don\'t tip at restaurants',
      'Stand on the left side of escalators (Tokyo)'
    ],
    weather: [
      { season: 'Spring', temp: '10-20°C', condition: 'Mild, cherry blossoms' },
      { season: 'Summer', temp: '25-35°C', condition: 'Hot and humid' },
      { season: 'Autumn', temp: '15-25°C', condition: 'Pleasant, fall colors' },
      { season: 'Winter', temp: '0-10°C', condition: 'Cold, possible snow' }
    ]
  },
  thailand: {
    name: 'Thailand',
    country: 'Thailand',
    description: 'Discover tropical beaches, ornate temples, and world-renowned cuisine in the Land of Smiles.',
    climate: 'Tropical monsoon climate. Dry season: November-February (cool & dry). Rainy season: May-October (monsoon). Hot season: March-May. Best time: November-February for ideal weather.',
    bestTime: 'November-February (dry season) - cool and pleasant. Avoid May-October rainy season for beach trips.',
    essentials: [
      'Lightweight, breathable clothing',
      'Modest attire for temples',
      'Sunscreen and insect repellent',
      'Waterproof bag for boat trips',
      'Comfortable sandals',
      'Thai SIM card',
      'Cash for street food',
      'First-aid kit'
    ],
    tips: [
      'Never touch someone\'s head',
      'Remove shoes before entering temples',
      'Don\'t point feet at Buddha images',
      'The King is highly revered',
      'Bargain at markets (politely)'
    ],
    weather: [
      { season: 'Dry (Nov-Feb)', temp: '20-30°C', condition: 'Best time - dry & pleasant' },
      { season: 'Hot (Mar-May)', temp: '30-40°C', condition: 'Very hot and humid' },
      { season: 'Rainy (May-Oct)', temp: '25-32°C', condition: 'Monsoon rains daily' }
    ]
  },
  italy: {
    name: 'Italy',
    country: 'Italy',
    description: 'Immerse yourself in Renaissance art, ancient ruins, world-class cuisine, and la dolce vita lifestyle.',
    climate: 'Mediterranean climate with regional variations. Northern Italy (Milan, Venice): cooler with cold winters. Central Italy (Rome, Florence): mild Mediterranean. Southern Italy & Islands (Sicily, Sardinia): hot summers, mild winters. Coastal areas are warmer year-round.',
    bestTime: 'April-June or September-October for pleasant weather. Summer can be very hot in the south.',
    essentials: [
      'Comfortable walking shoes',
      'Scarf for church visits',
      'Smart casual evening wear',
      'European power adapter',
      'Sunglasses and sun hat',
      'Light layers',
      'Anti-theft bag',
      'Italian phrasebook'
    ],
    tips: [
      'Dress code for churches (cover shoulders/knees)',
      'Aperitivo hour is sacred (6-8pm)',
      'Cappuccino only in the morning',
      'Validate train tickets before boarding',
      'Learn basic Italian greetings'
    ],
    weather: [
      { season: 'Spring', temp: '12-20°C', condition: 'Pleasant, occasional rain' },
      { season: 'Summer', temp: '25-35°C', condition: 'Hot south, warm north' },
      { season: 'Autumn', temp: '15-22°C', condition: 'Mild, harvest season' },
      { season: 'Winter', temp: '5-12°C', condition: 'Cold north, mild south' }
    ]
  },
  greece: {
    name: 'Greece',
    country: 'Greece',
    description: 'Explore ancient ruins, crystal-clear waters, and charming islands in the birthplace of Western civilization.',
    climate: 'Mediterranean climate with regional variations. Mainland (Athens): hot summers, mild winters. Islands (Santorini, Mykonos, Crete): warmer year-round. Northern Greece (Thessaloniki): cooler with occasional snow. Coastal and island areas are warmer than inland.',
    bestTime: 'April-June or September-October for ideal weather. July-August is peak season but very hot.',
    essentials: [
      'Comfortable walking shoes for ruins',
      'Swimwear and beach towel',
      'Sunscreen and sun hat',
      'Light, breathable clothing',
      'European power adapter',
      'Modest clothing for monasteries',
      'Water shoes for rocky beaches',
      'Greek phrasebook'
    ],
    tips: [
      'Dress modestly at religious sites',
      'Afternoon siesta (2-5pm) is common',
      'Tipping 5-10% is appreciated',
      'Book ferries in advance during summer',
      'Learn to say "Kalimera" (good morning)'
    ],
    weather: [
      { season: 'Spring', temp: '15-22°C', condition: 'Pleasant, wildflowers' },
      { season: 'Summer', temp: '28-38°C', condition: 'Hot & dry, islands cooler' },
      { season: 'Autumn', temp: '18-25°C', condition: 'Warm, fewer crowds' },
      { season: 'Winter', temp: '8-15°C', condition: 'Mild coast, cold north' }
    ]
  },
  indonesia: {
    name: 'Indonesia',
    country: 'Indonesia',
    description: 'Discover tropical paradise with stunning temples, pristine beaches, and vibrant cultural traditions across thousands of islands.',
    climate: 'Tropical climate with distinct seasons. Dry season: April-October (best time). Rainy season: November-March (daily afternoon storms). Best time to visit: May-September for ideal conditions.',
    bestTime: 'April-October (dry season) - sunny with low humidity. Avoid November-March wet season.',
    essentials: [
      'Sarong for temple visits',
      'Reef-safe sunscreen',
      'Mosquito repellent',
      'Light, modest clothing',
      'Water shoes',
      'Waterproof phone case',
      'Cash for small vendors',
      'First-aid kit'
    ],
    tips: [
      'Respect temple dress codes',
      'Learn about local customs and holidays',
      'Negotiate taxi fares beforehand',
      'Drink only bottled water',
      'Be patient with "island time"'
    ],
    weather: [
      { season: 'Dry (Apr-Oct)', temp: '27-30°C', condition: 'Best time - sunny, low humidity' },
      { season: 'Wet (Nov-Mar)', temp: '27-30°C', condition: 'Rainy season - afternoon storms' }
    ]
  },
  morocco: {
    name: 'Morocco',
    country: 'Morocco',
    description: 'Experience vibrant markets, ancient medinas, and stunning desert landscapes in this North African gem.',
    climate: 'Mediterranean coast, desert interior',
    bestTime: 'March-May or September-November',
    essentials: [
      'Modest clothing (shoulders/knees covered)',
      'Comfortable walking shoes',
      'Scarf or shawl',
      'Sunscreen and hat',
      'Cash in Dirhams',
      'Reusable water bottle',
      'Motion sickness medicine for mountain roads',
      'Flashlight for medina navigation'
    ],
    tips: [
      'Bargaining is expected in souks',
      'Dress modestly, especially in rural areas',
      'Learn basic French or Arabic phrases',
      'Friday is the holy day - some shops close',
      'Accept mint tea when offered - it\'s hospitality'
    ],
    weather: [
      { season: 'Spring', temp: '15-25°C', condition: 'Pleasant, flowers blooming' },
      { season: 'Summer', temp: '25-40°C', condition: 'Hot, especially inland' },
      { season: 'Autumn', temp: '18-28°C', condition: 'Warm, fewer tourists' },
      { season: 'Winter', temp: '8-18°C', condition: 'Mild coast, cold mountains' }
    ]
  },
  portugal: {
    name: 'Portugal',
    country: 'Portugal',
    description: 'Explore historic cities, golden beaches, and world-class wine regions along Europe\'s Atlantic coast.',
    climate: 'Mediterranean climate with regional variations. Southern Algarve: hottest and driest. Lisbon & Central: mild Mediterranean. Northern Porto & Douro: cooler with more rainfall. Atlantic coast is windier and slightly cooler than inland areas.',
    bestTime: 'May-October for beaches, year-round for cities. South is warmer than north.',
    essentials: [
      'Comfortable walking shoes',
      'Light layers for coastal breezes',
      'Swimwear',
      'European power adapter',
      'Sunscreen and sunglasses',
      'Rain jacket (for spring/fall)',
      'Cash for small cafes',
      'Portuguese phrasebook'
    ],
    tips: [
      'Lunch is the main meal (1-3pm)',
      'Most speak English in tourist areas',
      'Try local pastéis de nata',
      'Tipping is appreciated but not mandatory',
      'Book popular restaurants in advance'
    ],
    weather: [
      { season: 'Spring', temp: '15-22°C', condition: 'Pleasant, occasional rain' },
      { season: 'Summer', temp: '25-35°C', condition: 'Hot south, warm north' },
      { season: 'Autumn', temp: '18-25°C', condition: 'Warm, grape harvest' },
      { season: 'Winter', temp: '10-15°C', condition: 'Mild, more rain north' }
    ]
  },
  usa: {
    name: 'United States',
    country: 'USA',
    description: 'From iconic cities to stunning national parks, discover the diverse landscapes and cultures of America.',
    climate: 'Varies dramatically by region. Weather depends on state and location. New York: cold winters (-5-5°C), hot summers (25-32°C). California: mild year-round (15-28°C), warmer in SoCal. Florida: subtropical, warm year-round (20-33°C). Hawaii: tropical (22-30°C always). Las Vegas: desert extremes (5-42°C). Pacific Northwest: rainy, mild (8-22°C).',
    bestTime: 'Spring (April-May) or Fall (September-October) for most regions. California: year-round. Florida: winter to avoid humidity. National Parks: varies by location.',
    essentials: [
      'Power adapter (Type A/B)',
      'Credit cards (widely accepted)',
      'Comfortable walking shoes',
      'Layers for air conditioning',
      'Health insurance documents',
      'Valid ID/passport',
      'Sunscreen and sunglasses',
      'Reusable water bottle'
    ],
    tips: [
      'Tipping is expected (15-20%)',
      'Sales tax is added at checkout',
      'Book national parks accommodation early',
      'Distances are large - plan accordingly',
      'Carry ID - may be asked for age verification'
    ],
    weather: [
      { season: 'Spring', temp: '10-25°C', condition: 'Varies by state' },
      { season: 'Summer', temp: '25-40°C', condition: 'Hot south, mild north' },
      { season: 'Autumn', temp: '10-25°C', condition: 'Fall colors northeast' },
      { season: 'Winter', temp: '-10-20°C', condition: 'Cold north, mild south' }
    ]
  }
};

export default function PackingListLanding() {
  const { destination } = useParams<{ destination: string }>();
  const navigate = useNavigate();
  
  const data = destination ? destinationData[destination.toLowerCase()] : null;
  
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-xl font-bold mb-2">Destination Not Found</h1>
            <p className="text-muted-foreground mb-4">
              We're still working on packing lists for this destination.
            </p>
            <Button onClick={() => navigate('/')}>
              Explore OdyVoy
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pageTitle = `${data.name} Packing List | OdyVoy`;
  const pageDescription = `Complete ${data.name} packing list with weather tips and local customs. Plan your trip with OdyVoy.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://odyvoy.com/packing-list/${destination}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://odyvoy.com/packing-list/${destination}`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${data.name} Packing List - Complete Travel Guide`,
            "description": pageDescription,
            "author": {
              "@type": "Organization",
              "name": "OdyVoy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "url": "https://odyvoy.com"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": `${data.name} Packing Essentials`,
            "itemListElement": data.essentials.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex justify-between items-center gap-2">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 shrink-0" aria-label="Go to homepage">
              <Logo className="h-10 w-10 sm:h-14 sm:w-14 md:h-20 md:w-20" alt="OdyVoy logo" />
            </button>
            <div className="flex items-center gap-1 sm:gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-foreground h-9 px-2 sm:px-3" aria-label="Back to home">
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
              <ThemeToggle />
              <Button size="sm" onClick={() => navigate('/auth')} className="h-9">
                Get Started
              </Button>
            </div>
          </div>
        </header>

        <main>
          {/* Hero Section */}
          <section className="bg-gradient-to-b from-primary/10 to-background py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4" variant="secondary">
                <MapPin className="h-3 w-3 mr-1" />
                {data.country}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {data.name} Packing List
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                {data.description}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" onClick={() => navigate('/auth')}>
                  <Luggage className="h-5 w-5 mr-2" />
                  Create My Packing List
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/auth')}>
                  <Download className="h-5 w-5 mr-2" />
                  Download Free Checklist
                </Button>
              </div>
            </div>
          </section>

          {/* Weather Section */}
          <section className="py-12 px-4 bg-muted/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Thermometer className="h-6 w-6 text-primary" />
                Weather & Best Time to Visit
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sun className="h-5 w-5 text-amber-500" />
                      <span className="font-medium">Best Time</span>
                    </div>
                    <p className="text-muted-foreground">{data.bestTime}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Umbrella className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">Climate</span>
                    </div>
                    <p className="text-muted-foreground">{data.climate}</p>
                  </CardContent>
                </Card>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {data.weather.map((w, i) => (
                  <Card key={i}>
                    <CardContent className="p-4 text-center">
                      <p className="font-medium">{w.season}</p>
                      <p className="text-2xl font-bold text-primary my-1">{w.temp}</p>
                      <p className="text-xs text-muted-foreground">{w.condition}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Essentials Section */}
          <section className="py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Luggage className="h-6 w-6 text-primary" />
                Essential Packing Items
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.essentials.map((item, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Tips Section */}
          <section className="py-12 px-4 bg-muted/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Star className="h-6 w-6 text-primary" />
                Local Tips & Customs
              </h2>
              <div className="space-y-3">
                {data.tips.map((tip, i) => (
                  <div 
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-lg bg-background border"
                  >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 px-4 bg-gradient-to-b from-background to-primary/10">
            <div className="max-w-2xl mx-auto text-center">
              <Plane className="h-12 w-12 mx-auto text-primary mb-4" />
              <h2 className="text-3xl font-bold mb-4">
                Get Your Personalized {data.name} Packing List
              </h2>
              <p className="text-muted-foreground mb-8">
                OdyVoy's AI creates a custom packing list based on your trip dates, 
                activities, and personal preferences. Never forget an essential item again!
              </p>
              <Button size="lg" onClick={() => navigate('/auth')}>
                Create Free Account
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4">
          <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} OdyVoy. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <button onClick={() => navigate('/terms')} className="hover:text-foreground">
                Terms
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-foreground">
                Privacy
              </button>
              <button onClick={() => navigate('/blog')} className="hover:text-foreground">
                Blog
              </button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}